import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCwIcon } from "lucide-react";
import { useMemo, useSyncExternalStore, type ReactNode } from "react";

import { platformVersionQuery } from "../api/queries";
import { DeploymentUpdateContext } from "./deployment-update-context";
import { LoadedDeploymentVersionStore } from "./deployment-version-store";
import { Button } from "./ui/button";

export function DeploymentUpdateProvider({
  children,
  reloadPage = () => {
    window.location.reload();
  },
}: {
  children: ReactNode;
  reloadPage?: () => void;
}) {
  const queryClient = useQueryClient();
  const versionStore = useMemo(() => new LoadedDeploymentVersionStore(queryClient), [queryClient]);
  const loadedVersion = useSyncExternalStore(
    versionStore.subscribe,
    versionStore.getSnapshot,
    versionStore.getSnapshot,
  );
  const version = useQuery(platformVersionQuery);
  const currentVersion = version.data?.deploymentVersion ?? null;

  const value = useMemo(
    () => ({
      platformUpdateAvailable:
        loadedVersion !== null && currentVersion !== null && currentVersion !== loadedVersion,
      refreshPlatform: reloadPage,
    }),
    [currentVersion, loadedVersion, reloadPage],
  );

  return (
    <DeploymentUpdateContext.Provider value={value}>{children}</DeploymentUpdateContext.Provider>
  );
}

export function DeploymentUpdateNotice({
  kind,
  onRefresh,
}: {
  kind: "platform" | "tool";
  onRefresh: () => void;
}) {
  const isPlatform = kind === "platform";
  return (
    <section
      aria-live="polite"
      className="flex shrink-0 flex-col items-start justify-between gap-3 border-b border-primary/25 bg-primary/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:px-6"
      role="status"
    >
      <p className="m-0 font-medium text-foreground">
        {isPlatform
          ? "CCBox has been updated. Refresh CCBox to load the latest version."
          : "This tool has been updated. Refresh the tool to load the latest version."}
      </p>
      <Button size="sm" type="button" onClick={onRefresh}>
        <RefreshCwIcon aria-hidden="true" data-icon="inline-start" />
        {isPlatform ? "Refresh CCBox" : "Refresh tool"}
      </Button>
    </section>
  );
}
